import NiceModal, { useModal } from "@ebay/nice-modal-react"
import QRCode from "qrcode"
import { Check, Clipboard, Loader2, Share2 } from "lucide-react"
import { useEffect, useState } from "react"
import { type PairingSession, useComputers } from "../../computers/context"
import type { AccessLevel } from "../../computers/people"
import { getPlatformAdapter } from "../../platform/adapter"
import { useOptionalCodeStore } from "../../store/context"
import { copyTextToClipboard } from "../../utils/clipboard"
import { ProjectIcon } from "../sidebar/ProjectIcon"
import { Modal } from "../ui/Modal"
import { Segmented } from "./Segmented"
import { DURATIONS, SHARE_LEVELS, durationExpiry, expiresInWords } from "./sharingCopy"

interface ShareSheetProps {
    nodeId: string
    projects: Array<{ id: string; name: string; color?: string; glyph?: string; projectChars?: string; projectEmoji?: string }>
    initialProjectId?: string
    existingPeople?: string[]
}

type ShareLevel = Exclude<AccessLevel, "admin">

/** Past this many projects a grid of tiles stops being a glance and becomes a hunt. */
const TILE_LIMIT = 6

/**
 * Share a project with one person. The sheet reads top to bottom as a
 * sentence: share this project, with this person, letting them do this,
 * for this long. The result is one message to paste.
 */
export const ShareSheet = NiceModal.create(({ nodeId, projects, initialProjectId, existingPeople = [] }: ShareSheetProps) => {
    const modal = useModal()
    const computers = useComputers()
    const codeStore = useOptionalCodeStore()
    const profileSettings = codeStore?.personalSettingsStore?.settings
    const needsOwnName = !profileSettings?.current.profile?.name
    const [ownName, setOwnName] = useState(() => profileSettings?.current.profile?.name ?? "")
    const [personName, setPersonName] = useState("")
    const [projectId, setProjectId] = useState(initialProjectId ?? projects[0]?.id ?? "")
    const [level, setLevel] = useState<ShareLevel>("propose")
    const [duration, setDuration] = useState(DURATIONS[0]!.id)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [session, setSession] = useState<PairingSession | null>(null)
    const [copied, setCopied] = useState(false)
    const [qrCode, setQrCode] = useState<string | null>(null)
    const [now, setNow] = useState(() => Date.now())
    const project = projects.find((candidate) => candidate.id === projectId)
    const alreadyLinked = existingPeople.some((name) => name.trim().toLowerCase() === personName.trim().toLowerCase() && personName.trim())

    useEffect(() => {
        if (!session) return
        let active = true
        void QRCode.toDataURL(session.inviteUrl, { errorCorrectionLevel: "M", margin: 1, width: 320 }).then((value) => {
            if (active) setQrCode(value)
        })
        const interval = globalThis.setInterval(() => setNow(Date.now()), 15_000)
        return () => {
            active = false
            globalThis.clearInterval(interval)
        }
    }, [session])

    const create = async () => {
        if (!project) return
        const name = personName.trim()
        if (!name) {
            setError("Who is this for? Their name is what you'll see in People.")
            return
        }
        if (needsOwnName && !ownName.trim()) {
            setError("Add your name so they know who shared this.")
            return
        }
        setBusy(true)
        setError(null)
        try {
            if (needsOwnName && profileSettings) profileSettings.set({ profile: { ...profileSettings.current.profile, name: ownName.trim() } })
            const expiresAt = durationExpiry(duration)
            setSession(
                await computers.startShare(nodeId, {
                    personName: name,
                    level,
                    workspaceIds: [project.id],
                    projectName: project.name,
                    ...(expiresAt ? { expiresAt } : {}),
                })
            )
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
            setBusy(false)
        }
    }

    const copyMessage = async () => {
        if (!session?.share) return
        try {
            await copyTextToClipboard(session.share.message)
            setCopied(true)
            globalThis.setTimeout(() => setCopied(false), 1_500)
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        }
    }

    const shareMessage = async () => {
        if (!session?.share) return
        const result = await getPlatformAdapter().share({ title: "Shared on WildLark", text: session.share.message, url: session.inviteUrl })
        if (result.status === "unavailable") await copyMessage()
    }

    const close = () => {
        modal.resolve()
        void modal.hide()
    }

    return (
        <Modal title={session ? `Ready for ${personName.trim()}` : "Share a project"} onClose={close} size="md">
            {session ? (
                <div className="flex flex-col gap-5">
                    <p className="text-sm leading-6 text-muted">
                        Paste this into a chat with <span className="font-medium text-base-content">{personName.trim()}</span>. It opens once and expires in{" "}
                        {expiresInWords(session.expiresAt, now)}. Their access on <span className="font-medium text-base-content">{project?.name}</span> lasts{" "}
                        {DURATIONS.find((candidate) => candidate.id === duration)?.label.toLowerCase()}.
                    </p>
                    <div className="border border-border bg-base-200/60 p-4">
                        <pre className="whitespace-pre-wrap break-words font-sans text-[13px] leading-6 text-base-content [overflow-wrap:anywhere]">
                            {session.share?.message}
                        </pre>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            className="btn flex h-10 flex-1 items-center justify-center gap-2 bg-primary px-4 text-sm font-medium text-primary-content hover:bg-primary/90"
                            onClick={() => void copyMessage()}
                        >
                            {copied ? <Check className="h-4 w-4" /> : <Clipboard className="h-4 w-4" />}
                            {copied ? "Copied" : "Copy message"}
                        </button>
                        <button
                            type="button"
                            className="btn flex h-10 items-center justify-center gap-2 border border-border px-4 text-sm font-medium text-base-content hover:bg-base-200"
                            onClick={() => void shareMessage()}
                        >
                            <Share2 className="h-4 w-4" />
                            Share…
                        </button>
                    </div>
                    <div className="flex items-center gap-4 border-t border-border pt-4">
                        <div className="flex h-36 w-36 flex-none items-center justify-center bg-white p-1.5">
                            {qrCode ? (
                                <img src={qrCode} alt="Invite QR code" className="h-full w-full" />
                            ) : (
                                <Loader2 className="h-4 w-4 animate-spin text-black/40" />
                            )}
                        </div>
                        <p className="text-xs leading-5 text-muted">
                            In the same room? They can scan this instead. Either way, nothing here reveals your work; the invite only carries a one-time code.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="btn flex h-10 items-center justify-center border border-border text-sm font-medium text-base-content hover:bg-base-200"
                        onClick={close}
                    >
                        Done
                    </button>
                </div>
            ) : (
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">Project</span>
                        {projects.length > TILE_LIMIT ? (
                            <div className="flex items-center gap-3">
                                {project && (
                                    <ProjectIcon
                                        projectId={project.id}
                                        projectName={project.name}
                                        color={project.color}
                                        glyph={project.glyph}
                                        projectChars={project.projectChars}
                                        projectEmoji={project.projectEmoji}
                                    />
                                )}
                                <select
                                    className="input h-11 min-w-0 flex-1 border border-border bg-input px-3 text-sm text-base-content outline-none focus:border-primary"
                                    value={projectId}
                                    onChange={(event) => setProjectId(event.target.value)}
                                    aria-label="Project"
                                >
                                    {projects.map((candidate) => (
                                        <option key={candidate.id} value={candidate.id}>
                                            {candidate.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {projects.map((candidate) => {
                                    const selected = candidate.id === projectId
                                    return (
                                        <button
                                            type="button"
                                            key={candidate.id}
                                            className={`btn flex min-h-11 items-center gap-2 border px-3 text-left text-sm ${
                                                selected
                                                    ? "border-primary bg-primary/5 text-base-content"
                                                    : "border-border text-muted hover:bg-base-200 hover:text-base-content"
                                            }`}
                                            onClick={() => setProjectId(candidate.id)}
                                            aria-pressed={selected}
                                        >
                                            <ProjectIcon
                                                projectId={candidate.id}
                                                projectName={candidate.name}
                                                color={candidate.color}
                                                glyph={candidate.glyph}
                                                projectChars={candidate.projectChars}
                                                projectEmoji={candidate.projectEmoji}
                                            />
                                            <span className="truncate">{candidate.name}</span>
                                        </button>
                                    )
                                })}
                            </div>
                        )}
                    </div>

                    <div className={`grid gap-4 ${needsOwnName ? "sm:grid-cols-2" : ""}`}>
                        <label className="flex flex-col gap-2">
                            <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">With</span>
                            <input
                                className="input h-11 w-full border border-border bg-input px-3 text-sm text-base-content outline-none focus:border-primary"
                                value={personName}
                                onChange={(event) => setPersonName(event.target.value)}
                                placeholder="Sam"
                                autoFocus
                                autoComplete="off"
                                maxLength={80}
                            />
                        </label>
                        {needsOwnName && (
                            <label className="flex flex-col gap-2">
                                <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">From</span>
                                <input
                                    className="input h-11 w-full border border-border bg-input px-3 text-sm text-base-content outline-none focus:border-primary"
                                    value={ownName}
                                    onChange={(event) => setOwnName(event.target.value)}
                                    placeholder="Your name"
                                    autoComplete="name"
                                    maxLength={80}
                                />
                            </label>
                        )}
                    </div>
                    {alreadyLinked && (
                        <p className="-mt-3 border border-warning/40 bg-warning/10 px-3 py-2 text-xs leading-5 text-base-content">
                            {personName.trim()} is already linked. This invite adds a second entry; to change what they can do, use the menu beside their name
                            instead.
                        </p>
                    )}

                    <div className="flex flex-col gap-2">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">They can</span>
                        <Segmented<ShareLevel> options={SHARE_LEVELS} value={level} onChange={setLevel} label="Access level" />
                    </div>

                    <label className="flex flex-col gap-2">
                        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted">For</span>
                        <select
                            className="input h-11 w-full border border-border bg-input px-3 text-sm text-base-content outline-none focus:border-primary"
                            value={duration}
                            onChange={(event) => setDuration(event.target.value)}
                        >
                            {DURATIONS.map((candidate) => (
                                <option key={candidate.id} value={candidate.id}>
                                    {candidate.label}
                                </option>
                            ))}
                        </select>
                    </label>

                    {error && (
                        <div role="alert" className="border border-error/40 bg-error/10 px-3 py-2 text-sm text-error">
                            {error}
                        </div>
                    )}
                    <button
                        type="button"
                        className="btn flex h-11 w-full items-center justify-center gap-2 bg-primary text-sm font-medium text-primary-content disabled:opacity-50"
                        disabled={busy || !project}
                        onClick={() => void create()}
                    >
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                        Create invite
                    </button>
                </div>
            )}
        </Modal>
    )
})
